import React, { useMemo, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { ContactlessOutlined } from '@mui/icons-material';
import QRCode from 'qrcode';

const d = (data, key, fallback = '') => {
  const v = data?.[key];
  return v != null && String(v).trim() !== '' ? String(v).trim() : fallback;
};

function SplitBackground({ bg, tokens }) {
  if (!bg || bg.type !== 'split') return null;
  const lw = bg.leftWidthPercent ?? 33;
  const lc = bg.leftColor || '#000000';
  const rc = bg.rightColor || tokens.background;
  return (
    <>
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: `${lw}%`,
          bgcolor: lc,
          zIndex: 0,
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          left: `${lw}%`,
          top: 0,
          right: 0,
          bottom: 0,
          zIndex: 0,
          background: `linear-gradient(165deg, ${rc} 0%, #0c1628 42%, #080d18 100%)`,
        }}
      />
    </>
  );
}

function PatternLayer({ pattern, blendColor = '#0A0E1A' }) {
  if (!pattern?.src) return null;
  const anchor = pattern.anchor || 'right';
  const isCenter = anchor === 'center';
  const isCorner =
    anchor === 'top-right' ||
    anchor === 'top-left' ||
    anchor === 'bottom-right' ||
    anchor === 'bottom-left' ||
    anchor === 'bottom' ||
    anchor === 'top';
  const anchorSide = anchor === 'left' ? 'left' : 'right';
  const w = pattern.widthPercent != null ? Number(pattern.widthPercent) : isCenter || anchor === 'bottom' || anchor === 'top' ? 100 : 55;
  const h = pattern.heightPercent != null ? Number(pattern.heightPercent) : null;
  const fadeInside = pattern.fadeInside;
  const bc = pattern.maskBlendColor || blendColor;
  const fadeMask =
    pattern.fadeMask === false || isCenter || isCorner
      ? null
      : pattern.fadeMask ||
        (fadeInside === 'left'
          ? `linear-gradient(90deg, transparent 0%, ${bc}26 8%, ${bc}8c 26%, ${bc}e6 40%, ${bc} 52%)`
          : fadeInside === 'right'
            ? `linear-gradient(270deg, transparent 0%, ${bc}26 8%, ${bc}8c 26%, ${bc}e6 40%, ${bc} 52%)`
            : null);

  let boxPos;
  if (isCenter) {
    boxPos = { left: 0, right: 0, top: 0, bottom: 0, width: '100%' };
  } else if (anchor === 'top-right') {
    boxPos = {
      top: pattern.offsetTopPercent != null ? `${pattern.offsetTopPercent}%` : 0,
      right: pattern.offsetRightPercent != null ? `${pattern.offsetRightPercent}%` : 0,
      left: 'auto',
      bottom: 'auto',
      width: `${w}%`,
      height: h != null ? `${h}%` : `${w}%`,
    };
  } else if (anchor === 'top-left') {
    boxPos = {
      top: pattern.offsetTopPercent != null ? `${pattern.offsetTopPercent}%` : 0,
      left: pattern.offsetLeftPercent != null ? `${pattern.offsetLeftPercent}%` : 0,
      right: 'auto',
      bottom: 'auto',
      width: `${w}%`,
      height: h != null ? `${h}%` : `${w}%`,
    };
  } else if (anchor === 'bottom-right') {
    boxPos = {
      bottom: pattern.offsetBottomPercent != null ? `${pattern.offsetBottomPercent}%` : 0,
      right: pattern.offsetRightPercent != null ? `${pattern.offsetRightPercent}%` : 0,
      left: 'auto',
      top: 'auto',
      width: `${w}%`,
      height: h != null ? `${h}%` : `${w}%`,
    };
  } else if (anchor === 'bottom-left') {
    boxPos = {
      bottom: pattern.offsetBottomPercent != null ? `${pattern.offsetBottomPercent}%` : 0,
      left: pattern.offsetLeftPercent != null ? `${pattern.offsetLeftPercent}%` : 0,
      right: 'auto',
      top: 'auto',
      width: `${w}%`,
      height: h != null ? `${h}%` : `${w}%`,
    };
  } else if (anchor === 'bottom') {
    const fullBleed = w >= 100 && pattern.offsetLeftPercent == null && pattern.offsetRightPercent == null;
    boxPos = fullBleed
      ? {
          bottom: pattern.offsetBottomPercent != null ? `${pattern.offsetBottomPercent}%` : 0,
          left: 0,
          right: 0,
          top: 'auto',
          width: '100%',
          height: h != null ? `${h}%` : '55%',
        }
      : {
          bottom: pattern.offsetBottomPercent != null ? `${pattern.offsetBottomPercent}%` : 0,
          left: pattern.offsetLeftPercent != null ? `${pattern.offsetLeftPercent}%` : '50%',
          right: pattern.offsetRightPercent != null ? `${pattern.offsetRightPercent}%` : 'auto',
          top: 'auto',
          width: `${w}%`,
          height: h != null ? `${h}%` : '55%',
          ...(pattern.offsetLeftPercent == null && pattern.offsetRightPercent == null
            ? { transform: 'translateX(-50%)' }
            : {}),
        };
  } else if (anchor === 'top') {
    boxPos = {
      top: pattern.offsetTopPercent != null ? `${pattern.offsetTopPercent}%` : 0,
      left: 0,
      right: 0,
      bottom: 'auto',
      width: '100%',
      height: h != null ? `${h}%` : '55%',
    };
  } else {
    boxPos = { top: 0, bottom: 0, [anchorSide]: 0, width: `${w}%` };
  }

  const bgPos =
    pattern.backgroundPosition ||
    (anchor === 'top-right'
      ? 'top right'
      : anchor === 'top-left'
        ? 'top left'
        : anchor === 'bottom-right'
          ? 'bottom right'
          : anchor === 'bottom-left'
            ? 'bottom left'
            : anchor === 'bottom'
              ? 'bottom center'
              : anchor === 'top'
                ? 'top center'
                : isCenter
                  ? 'center center'
                  : anchorSide === 'right'
                    ? 'right center'
                    : 'left center');
  const bgSize = pattern.backgroundSize || (isCorner ? 'contain' : 'cover');
  return (
    <Box
      aria-hidden
      sx={{
        position: 'absolute',
        ...boxPos,
        zIndex: 1,
        backgroundImage: `url(${pattern.src})`,
        backgroundSize: bgSize,
        backgroundPosition: bgPos,
        backgroundRepeat: 'no-repeat',
        pointerEvents: 'none',
        opacity: pattern.opacity ?? 0.92,
        ...(pattern.mixBlendMode
          ? { mixBlendMode: pattern.mixBlendMode }
          : {}),
        ...(pattern.filter ? { filter: pattern.filter } : {}),
        ...(pattern.transform ? { transform: pattern.transform } : {}),
        ...(fadeMask
          ? {
              maskImage: fadeMask,
              WebkitMaskImage: fadeMask,
              maskSize: '100% 100%',
              WebkitMaskSize: '100% 100%',
            }
          : {}),
      }}
    />
  );
}

/** Rounded-square app mark + letter (default white on accent). */
function WordmarkLogoMark({ accent, letter, letterColor = '#fff', sx: sxProp }) {
  const ch = (letter && String(letter).trim().charAt(0).toUpperCase()) || 'G';
  return (
    <Box
      sx={{
        width: 'clamp(56px, 8.5cqw, 132px)',
        height: 'clamp(56px, 8.5cqw, 132px)',
        borderRadius: 'clamp(12px, 1.1cqw, 22px)',
        bgcolor: accent,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        boxShadow: `0 0 24px ${accent}55`,
        ...sxProp,
      }}
      aria-hidden
    >
      <Typography
        sx={{
          color: letterColor,
          fontWeight: 800,
          fontSize: 'clamp(1.35rem, 4.8cqw, 3.35rem)',
          lineHeight: 1,
          fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        }}
      >
        {ch}
      </Typography>
    </Box>
  );
}

function SlotView({ slot, tokens, data, qrDataUrl, qrValue }) {
  const st = slot.style || {};
  const pos = {
    position: 'absolute',
    ...(st.top != null ? { top: st.top } : {}),
    ...(st.bottom != null ? { bottom: st.bottom } : {}),
    ...(st.left != null ? { left: st.left } : {}),
    ...(st.right != null ? { right: st.right } : {}),
    ...(st.maxWidth != null ? { maxWidth: st.maxWidth } : {}),
    ...(st.maxHeight != null ? { maxHeight: st.maxHeight } : {}),
    ...(st.textAlign != null ? { textAlign: st.textAlign } : {}),
    ...(st.transform != null ? { transform: st.transform } : {}),
    ...(st.opacity != null ? { opacity: Number(st.opacity) } : {}),
  };

  switch (slot.type) {
    case 'logo': {
      const bind = slot.bind || {};
      const imgKey = bind.imageKey || 'profileImageUrl';
      const textKey = bind.textKey || 'company';
      const fbKey = bind.fallbackTextKey || 'name';
      const img = d(data, imgKey, '');
      const text = d(data, textKey, '') || d(data, fbKey, 'Your brand');
      if (img) {
        return (
          <Box sx={{ ...pos, zIndex: 2, display: 'flex', alignItems: 'center', maxHeight: st.maxHeight || '15%' }}>
            <Box
              component="img"
              src={img}
              alt=""
              sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }}
            />
          </Box>
        );
      }
      return (
        <Box sx={{ ...pos, zIndex: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              border: `2px solid ${tokens.accent}`,
              borderTopColor: 'transparent',
              transform: 'rotate(-35deg)',
            }}
          />
          <Typography
            sx={{
              color: tokens.textPrimary,
              fontWeight: 600,
              fontSize: { xs: 'clamp(0.7rem, 2.8vw, 1.15rem)', sm: 'clamp(0.85rem, 1.9vw, 1.35rem)' },
              letterSpacing: '0.02em',
              lineHeight: 1.2,
            }}
          >
            {text}
          </Typography>
        </Box>
      );
    }
    case 'wordmarkBrand': {
      const bind = slot.bind || {};
      const textKey = bind.textKey || 'company';
      const fbKey = bind.fallbackTextKey || 'name';
      const text = d(data, textKey, '') || d(data, fbKey, 'Logoipsum');
      const taglineKey = bind.taglineKey;
      const rawFromData = taglineKey ? d(data, taglineKey, '') : '';
      const taglineFb = bind.taglineFallback != null ? String(bind.taglineFallback) : '';
      const tagline = String(rawFromData).trim() || String(taglineFb).trim();
      const bannerKey = bind.bannerKey != null ? bind.bannerKey : 'bannerImageUrl';
      const banner = d(data, bannerKey, '');
      const letter =
        (bind.logoLetterKey && d(data, bind.logoLetterKey, '')) ||
        (bind.logoLetter != null && String(bind.logoLetter).trim() !== '' ? String(bind.logoLetter).trim() : '') ||
        text ||
        'G';
      const logoMaxH = st.logoMaxHeightPercent != null ? `${Number(st.logoMaxHeightPercent)}%` : '12%';
      const logoMaxW = st.logoMaxWidthPercent != null ? `${Number(st.logoMaxWidthPercent)}%` : '36%';
      const companyTypography = {
        color: st.companyColor != null ? st.companyColor : tokens.textPrimary,
        fontWeight: 700,
        fontSize:
          st.companyFontSize != null
            ? st.companyFontSize
            : 'clamp(22px, 2.2cqw, 64px)',
        letterSpacing: st.companyUppercase ? '0.06em' : '0.01em',
        textTransform: st.companyUppercase ? 'uppercase' : 'none',
        lineHeight: 1.28,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      };
      const brandAlign = st.alignItems || (st.textAlign === 'center' ? 'center' : 'flex-start');
      if (banner) {
        return (
          <Box
            sx={{
              ...pos,
              zIndex: 2,
              display: 'flex',
              flexDirection: 'row',
              alignItems: brandAlign,
              justifyContent: st.justifyContent || (st.textAlign === 'center' ? 'center' : 'flex-start'),
              gap: st.brandRowGap != null ? Number(st.brandRowGap) : 2,
              flexWrap: st.allowBrandWrap === false ? 'nowrap' : 'wrap',
            }}
          >
            <Box
              sx={{
                flexShrink: 0,
                display: 'block',
                maxHeight: logoMaxH,
                maxWidth: logoMaxW,
                borderRadius: st.logoBorderRadius != null ? `${Number(st.logoBorderRadius)}px` : '14px',
                overflow: 'hidden',
                border: '1px solid rgba(255,255,255,0.1)',
                boxShadow: '0 0 0 1px rgba(0,103,255,0.18)',
                bgcolor: 'rgba(0,0,0,0.18)',
              }}
            >
              <Box
                component="img"
                src={banner}
                alt=""
                sx={{
                  display: 'block',
                  maxHeight: '100%',
                  maxWidth: '100%',
                  width: 'auto',
                  height: 'auto',
                  objectFit: 'contain',
                  objectPosition: 'center',
                }}
              />
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                minWidth: 0,
                flex: '1 1 0%',
              }}
            >
              <Typography
                sx={{
                  ...companyTypography,
                  minWidth: st.companyMinWidth != null ? st.companyMinWidth : '38%',
                  maxWidth: st.companyTextMaxWidth != null ? st.companyTextMaxWidth : '100%',
                }}
              >
                {st.companyUppercase ? String(text).toUpperCase() : text}
              </Typography>
              {tagline ? (
                <Typography
                  sx={{
                    mt: 0.35,
                    color: st.taglineColor != null ? st.taglineColor : 'rgba(255,255,255,0.82)',
                    fontWeight: 400,
                    fontSize:
                      st.taglineFontSize != null
                        ? st.taglineFontSize
                        : 'clamp(14px, 1.35cqw, 28px)',
                    lineHeight: 1.35,
                    letterSpacing: '0.02em',
                    fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                    maxWidth: '100%',
                  }}
                >
                  {tagline}
                </Typography>
              ) : null}
            </Box>
          </Box>
        );
      }
      return (
        <Box
          sx={{
            ...pos,
            zIndex: 2,
            display: 'flex',
            flexDirection: 'row',
            alignItems: brandAlign,
            justifyContent: st.justifyContent || (st.textAlign === 'center' ? 'center' : 'flex-start'),
            gap: { xs: 1.25, sm: 1.75 },
          }}
        >
          {st.showLogoMark === false ? null : (
            <WordmarkLogoMark
              accent={st.logoMarkBg != null ? st.logoMarkBg : tokens.accent}
              letter={letter}
              letterColor={st.logoMarkLetterColor != null ? st.logoMarkLetterColor : '#fff'}
              sx={{ mt: 0.15, flexShrink: 0 }}
            />
          )}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: brandAlign === 'center' ? 'center' : 'flex-start',
              minWidth: 0,
              flex: st.textAlign === 'center' || st.showLogoMark === false ? '0 1 auto' : '1 1 0%',
            }}
          >
            <Typography sx={companyTypography}>
              {st.companyUppercase ? String(text).toUpperCase() : text}
            </Typography>
            {tagline ? (
              <Typography
                sx={{
                  mt: 0.35,
                  color: st.taglineColor != null ? st.taglineColor : 'rgba(255,255,255,0.82)',
                  fontWeight: 400,
                  fontSize:
                    st.taglineFontSize != null
                      ? st.taglineFontSize
                      : 'clamp(14px, 1.35cqw, 28px)',
                  lineHeight: 1.35,
                  letterSpacing: '0.02em',
                  fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                  maxWidth: '100%',
                }}
              >
                {tagline}
              </Typography>
            ) : null}
          </Box>
        </Box>
      );
    }
    case 'bannerStrip': {
      const bind = slot.bind || {};
      const url = d(data, bind.bannerKey || 'bannerImageUrl', '');
      const hp = st.heightPercent != null ? Number(st.heightPercent) : 11;
      if (!url) return null;
      return (
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: `${hp}%`,
            zIndex: 3,
            overflow: 'hidden',
            borderTop: `1px solid rgba(255,255,255,0.06)`,
          }}
        >
          <Box
            component="img"
            src={url}
            alt=""
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'center',
              display: 'block',
            }}
          />
        </Box>
      );
    }
    case 'bannerBrand': {
      const bind = slot.bind || {};
      const banner = d(data, bind.bannerKey || 'bannerImageUrl', '');
      const textKey = bind.textKey || 'company';
      const fbKey = bind.fallbackTextKey || 'name';
      const text = d(data, textKey, '') || d(data, fbKey, 'Logoipsum');
      const w = st.widthPercent != null ? `${st.widthPercent}%` : '44%';
      const h = st.heightPercent != null ? `${st.heightPercent}%` : '14%';
      const br = st.borderRadius != null ? Number(st.borderRadius) : 4;
      if (banner) {
        return (
          <Box
            sx={{
              ...pos,
              zIndex: 2,
              width: w,
              height: h,
              borderRadius: `${br}px`,
              overflow: 'hidden',
              border: `1px solid rgba(255,255,255,0.08)`,
              boxShadow: `0 0 0 1px rgba(0,103,255,0.15)`,
            }}
          >
            <Box
              component="img"
              src={banner}
              alt=""
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'center',
                display: 'block',
              }}
            />
          </Box>
        );
      }
      return (
        <Box sx={{ ...pos, zIndex: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <WordmarkLogoMark accent={tokens.accent} letter={text} />
          <Typography
            sx={{
              color: tokens.textPrimary,
              fontWeight: 500,
              fontSize: { xs: 'clamp(0.95rem, 3.2vw, 1.35rem)', sm: 'clamp(1.05rem, 2.2vw, 1.55rem)' },
              letterSpacing: '0.01em',
              lineHeight: 1.15,
              fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
            }}
          >
            {text}
          </Typography>
        </Box>
      );
    }
    case 'nfcBadge': {
      const badgeSrc = slot.imageSrc;
      if (badgeSrc) {
        const mh = st.maxHeight != null ? st.maxHeight : '16%';
        const mw = st.maxWidth != null ? st.maxWidth : '42%';
        // Size the wrapper so small source PNGs (e.g. 110×68) scale up on the artboard.
        return (
          <Box
            sx={{
              ...pos,
              zIndex: 2,
              display: 'flex',
              alignItems: 'center',
              height: st.height != null ? st.height : mh,
              maxWidth: mw,
              width: 'auto',
            }}
          >
            <Box
              component="img"
              src={badgeSrc}
              alt=""
              sx={{
                display: 'block',
                height: '100%',
                width: 'auto',
                maxWidth: '100%',
                objectFit: 'contain',
                ...(st.filter ? { filter: st.filter } : {}),
              }}
            />
          </Box>
        );
      }
      return (
        <Box
          sx={{
            ...pos,
            zIndex: 2,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: { xs: 0.75, sm: 1 },
          }}
        >
          <Typography
            component="span"
            sx={{
              color: tokens.textPrimary,
              fontWeight: 700,
              fontSize: { xs: 'clamp(0.8rem, 2.6vw, 1rem)', sm: 'clamp(0.9rem, 1.65vw, 1.1rem)' },
              letterSpacing: '0.06em',
              lineHeight: 1,
              fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
            }}
          >
            NFC
          </Typography>
          <ContactlessOutlined
            sx={{
              fontSize: { xs: 30, sm: 38 },
              color: tokens.textPrimary,
              opacity: 1,
            }}
          />
        </Box>
      );
    }
    case 'accentLine': {
      const wp = st.widthPercent ?? 45;
      const variant = st.variant || 'fadeLeft';
      // Prefer slot color so circuit front line stays mock blue (#0067FF), not theme accent.
      const lineColor = st.color || tokens.accent || '#0067FF';
      const lineRgb = (() => {
        const h = String(lineColor).replace('#', '').trim();
        if (h.length === 3) {
          return {
            r: parseInt(h[0] + h[0], 16),
            g: parseInt(h[1] + h[1], 16),
            b: parseInt(h[2] + h[2], 16),
          };
        }
        if (h.length === 6) {
          return {
            r: parseInt(h.slice(0, 2), 16),
            g: parseInt(h.slice(2, 4), 16),
            b: parseInt(h.slice(4, 6), 16),
          };
        }
        return { r: 0, g: 103, b: 255 };
      })();
      const rgba = (a) => `rgba(${lineRgb.r},${lineRgb.g},${lineRgb.b},${a})`;
      if (variant === 'solidFull') {
        const h = st.heightPx != null ? `${st.heightPx}px` : '3px';
        return (
          <Box
            sx={{
              ...pos,
              zIndex: 2,
              width: `${wp}%`,
              height: h,
              minHeight: '2px',
              bgcolor: lineColor,
              borderRadius: 0,
            }}
          />
        );
      }
      if (variant === 'thinBlendFull') {
        const h = st.heightPx != null ? `${Number(st.heightPx)}px` : '3px';
        return (
          <Box
            sx={{
              ...pos,
              zIndex: 2,
              left: 0,
              right: 0,
              width: '100%',
              maxWidth: '100%',
              height: h,
              minHeight: '2px',
              background: `linear-gradient(90deg, ${lineColor} 0%, ${lineColor} 14%, ${rgba(0.55)} 32%, ${rgba(0.12)} 55%, transparent 90%)`,
              borderRadius: 0,
            }}
          />
        );
      }
      const hp = st.heightPercent != null ? `${st.heightPercent}%` : '4px';
      return (
        <Box
          sx={{
            ...pos,
            zIndex: 2,
            width: `${wp}%`,
            height: hp,
            minHeight: '3px',
            background: `linear-gradient(90deg, ${lineColor} 0%, ${lineColor} 28%, transparent 100%)`,
            borderRadius: 0,
          }}
        />
      );
    }
    case 'nameTitle': {
      const bind = slot.bind || {};
      const rawName = d(data, bind.nameKey || 'name', 'Your name');
      const name =
        st.nameUppercase === false ? rawName : String(rawName).toUpperCase();
      const title = d(data, bind.titleKey || 'title', 'Your title');
      const showTitle = st.showTitle !== false;
      const nameFs =
        st.nameFontSize != null
          ? st.nameFontSize
          : 'clamp(28px, 3.5cqw, 80px)';
      const titleFs =
        st.titleFontSize != null
          ? st.titleFontSize
          : 'clamp(20px, 2.4cqw, 52px)';
      const titleMt = st.titleMarginTop != null ? Number(st.titleMarginTop) : 1.5;
      const nameWeight = st.nameFontWeight != null ? st.nameFontWeight : 800;
      const content = (
        <>
          <Typography
            sx={{
              color: st.nameColor != null ? st.nameColor : tokens.textPrimary,
              fontWeight: nameWeight,
              fontSize: nameFs,
              fontStyle: st.nameFontStyle || 'normal',
              letterSpacing: st.nameUppercase === false ? '0.01em' : '0.06em',
              lineHeight: 1.12,
              fontFamily:
                st.nameFontFamily ||
                'Georgia, "Times New Roman", Times, serif',
            }}
          >
            {name}
          </Typography>
          {showTitle ? (
            <Typography
              sx={{
                color: st.titleColor != null ? st.titleColor : tokens.subtitleColor || tokens.accent,
                fontWeight: 600,
                fontSize: titleFs,
                mt: titleMt,
                lineHeight: 1.3,
                fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
              }}
            >
              {title}
            </Typography>
          ) : null}
        </>
      );
      if (st.nameStrip) {
        return (
          <Box
            sx={{
              ...pos,
              zIndex: 2,
              px: 2,
              py: 1.25,
              maxWidth: st.maxWidth || '75%',
              background:
                'linear-gradient(90deg, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.75) 55%, rgba(255,255,255,0) 100%)',
              borderRadius: '2px',
            }}
          >
            {content}
          </Box>
        );
      }
      return <Box sx={{ ...pos, zIndex: 2 }}>{content}</Box>;
    }
    case 'decorRing': {
      const size = st.sizePercent != null ? Number(st.sizePercent) : 50;
      const bw = st.borderWidth != null ? Number(st.borderWidth) : 5;
      return (
        <Box
          aria-hidden
          sx={{
            ...pos,
            zIndex: 1,
            width: `${size}%`,
            aspectRatio: '1',
            borderRadius: '50%',
            border: `${bw}px solid ${st.color || '#5CE1FF'}`,
            bgcolor: st.fill != null ? st.fill : 'rgba(180, 165, 230, 0.4)',
            boxSizing: 'border-box',
            pointerEvents: 'none',
          }}
        />
      );
    }
    case 'qr': {
      return (
        <ThemedQrSlot
          style={st}
          tokens={tokens}
          pos={pos}
          qrDataUrl={qrDataUrl}
          qrValue={qrValue}
        />
      );
    }
    default:
      return null;
  }
}

/** Colored QR matching theme tokens / slot style (qrColor, qrLight, qrFrameBg). */
function ThemedQrSlot({ style: st = {}, tokens, pos, qrDataUrl, qrValue }) {
  const sp = st.sizePercent ?? 16;
  const minQr = st.minWidthPx != null ? Number(st.minWidthPx) : 120;
  const maxQr =
    st.maxWidthPx != null ? `${Number(st.maxWidthPx)}px` : `min(40cqw, 420px)`;
  const qrRadius = st.borderRadius != null ? Number(st.borderRadius) : 0.5;
  const frameBg = st.qrFrameBg != null ? st.qrFrameBg : '#fff';
  const framePad = st.qrFramePadding != null ? Number(st.qrFramePadding) : 0.75;
  const dark =
    st.qrColor ||
    st.qrDark ||
    tokens?.accent ||
    '#000000';
  const light = st.qrLight != null ? st.qrLight : '#ffffff';
  const [src, setSrc] = useState(qrDataUrl || '');

  useEffect(() => {
    const payload = qrValue != null && String(qrValue).trim() !== '' ? String(qrValue).trim() : '';
    if (!payload) {
      setSrc(qrDataUrl || '');
      return undefined;
    }
    let cancelled = false;
    QRCode.toDataURL(payload, {
      width: 512,
      margin: 1,
      color: { dark, light },
      errorCorrectionLevel: 'M',
    })
      .then((url) => {
        if (!cancelled) setSrc(url);
      })
      .catch(() => {
        if (!cancelled) setSrc(qrDataUrl || '');
      });
    return () => {
      cancelled = true;
    };
  }, [qrValue, dark, light, qrDataUrl]);

  return (
    <Box
      sx={{
        ...pos,
        zIndex: 2,
        width: `${sp}%`,
        aspectRatio: '1',
        minWidth: minQr,
        maxWidth: maxQr,
        bgcolor: frameBg,
        p: framePad,
        borderRadius: qrRadius,
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {src ? (
        <Box component="img" src={src} alt="" sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
      ) : (
        <Typography variant="caption" sx={{ color: '#64748b', textAlign: 'center', px: 0.5 }}>
          QR
        </Typography>
      )}
    </Box>
  );
}

/**
 * One printable face driven by theme JSON (from API or public/print-themes/layouts.json).
 */
export function NfcPrintFace({ face, tokens, qrDataUrl, qrValue, data, pixelWidth = null, pixelHeight = null, aspectRatio = '1051 / 670' }) {
  const br = face.borderRight && face.borderRight !== 'none' ? face.borderRight : undefined;
  const bl = face.borderLeft && face.borderLeft !== 'none' ? face.borderLeft : undefined;
  const split = face.background?.type === 'split';
  const baseBg = split ? (face.background?.rightColor || tokens.background) : tokens.background;
  const fixed =
    Number.isFinite(pixelWidth) &&
    Number.isFinite(pixelHeight) &&
    pixelWidth > 0 &&
    pixelHeight > 0;
  return (
    <Box
      sx={{
        position: 'relative',
        containerType: 'size',
        ...(fixed
          ? {
              width: `${pixelWidth}px`,
              height: `${pixelHeight}px`,
              minWidth: `${pixelWidth}px`,
              minHeight: `${pixelHeight}px`,
              flexShrink: 0,
            }
          : {
              width: '100%',
              aspectRatio,
            }),
        ...(face.surfaceGradient
          ? {
              background: face.surfaceGradient,
            }
          : { bgcolor: baseBg }),
        boxShadow: tokens.shadow,
        borderRight: br,
        borderLeft: bl,
        overflow: 'hidden',
        borderRadius: 0,
        WebkitPrintColorAdjust: 'exact',
        printColorAdjust: 'exact',
      }}
    >
      <SplitBackground bg={face.background} tokens={tokens} />
      <PatternLayer pattern={face.pattern} blendColor={tokens.background} />
      {face.pattern2 ? <PatternLayer pattern={face.pattern2} blendColor={tokens.background} /> : null}
      {(face.slots || []).map((slot) => (
        <SlotView key={slot.id} slot={slot} tokens={tokens} data={data} qrDataUrl={qrDataUrl} qrValue={qrValue} />
      ))}
    </Box>
  );
}

/**
 * Front + back for one theme; scales to max width.
 * @param {string[]} [faceIds] — if set, only these face ids are rendered (e.g. ['front'] for gallery).
 * @param {boolean} [showFaceLabels=true] — "Front" / "Back" captions above each face.
 */
export default function NfcPrintThemeCanvas({
  theme,
  cardData,
  qrDataUrl,
  qrValue,
  maxWidth = 920,
  faceIds = null,
  showFaceLabels = true,
  /** When set, each face renders at exact CSS px (print artboard size). */
  cardPixelSize = null,
  /** If true, front/back stack vertically (recommended when cardPixelSize is set). */
  previewStackVertical = false,
  /**
   * When using cardPixelSize, max width (px) of the on-screen preview; artboard is scaled by min(1, fitMaxWidth / printWidth) so it never appears wider than this (print geometry uses theme canvas px, e.g. 2102×1346).
   * Pass 0 or null for no scaling (full pixel size — very wide on screen).
   */
  fitMaxWidth = 400,
}) {
  if (!theme?.faces?.length) return null;
  const arW = Number(theme?.canvas?.width) || 1051;
  const arH = Number(theme?.canvas?.height) || 670;
  const aspectRatio = `${arW} / ${arH}`;
  const tokens = {
    background: theme.tokens?.background ?? '#0A0E1A',
    accent: theme.tokens?.accent ?? '#0067FF',
    textPrimary: theme.tokens?.textPrimary ?? '#FFFFFF',
    subtitleColor: theme.tokens?.subtitleColor ?? theme.tokens?.accent ?? '#0067FF',
    shadow: theme.tokens?.shadow ?? '0px -4px 110px 0px rgba(190, 219, 255, 0.14)',
  };
  const offsetTop = theme.canvas?.offsetTop ?? 0;
  const cw = cardPixelSize?.width;
  const ch = cardPixelSize?.height;
  const useFixedPx =
    cardPixelSize != null &&
    Number.isFinite(Number(cw)) &&
    Number.isFinite(Number(ch)) &&
    Number(cw) > 0 &&
    Number(ch) > 0;
  const pxW = useFixedPx ? Number(cw) : null;
  const pxH = useFixedPx ? Number(ch) : null;
  const faces = (theme.faces || []).filter((f) => {
    if (!faceIds || !faceIds.length) return true;
    return faceIds.includes(f.id);
  });
  if (!faces.length) return null;

  const rowOrCol =
    previewStackVertical || useFixedPx
      ? 'column'
      : { xs: 'column', md: 'row' };

  const gapPx = 24;
  const offsetPx = offsetTop ? Number(offsetTop) : 0;
  const contentWidth = useFixedPx ? pxW : null;

  const useFit = Boolean(useFixedPx && fitMaxWidth != null && Number(fitMaxWidth) > 0 && contentWidth);

  const fitScale = useMemo(() => {
    if (!useFit || !contentWidth) return 1;
    return Math.min(1, Number(fitMaxWidth) / contentWidth);
  }, [useFit, contentWidth, fitMaxWidth]);

  const faceLabel = (face) =>
    showFaceLabels ? (
      <Typography
        component="div"
        variant="subtitle2"
        sx={{
          display: 'block',
          mb: 0.75,
          fontWeight: 700,
          fontSize: '0.9375rem',
          lineHeight: 1.3,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: 'grey.900',
        }}
      >
        {face.label || face.id}
      </Typography>
    ) : null;

  const scaledFaceBlock = (face) => (
    <Box
      sx={{
        width: contentWidth * fitScale,
        height: (offsetPx + pxH) * fitScale,
        position: 'relative',
        overflow: 'hidden',
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: contentWidth,
          height: offsetPx + pxH,
          transform: `scale(${fitScale})`,
          transformOrigin: 'top left',
        }}
      >
        <Box sx={{ position: 'relative', mt: offsetTop ? `${offsetTop}px` : 0 }}>
          <NfcPrintFace
            face={face}
            tokens={tokens}
            data={cardData}
            qrDataUrl={qrDataUrl}
            qrValue={qrValue}
            pixelWidth={pxW}
            pixelHeight={pxH}
            aspectRatio={aspectRatio}
          />
        </Box>
      </Box>
    </Box>
  );

  const facesRow = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: rowOrCol,
        gap: useFixedPx ? `${gapPx}px` : { xs: 3, md: 4 },
        alignItems: 'flex-start',
        justifyContent: useFixedPx ? 'flex-start' : 'center',
        width: useFixedPx && contentWidth ? `${contentWidth}px` : '100%',
        maxWidth: useFixedPx && contentWidth ? `${contentWidth}px` : '100%',
      }}
    >
      {faces.map((face) => (
        <Box
          key={face.id}
          sx={{
            flex: useFixedPx ? '0 0 auto' : '1 1 0',
            minWidth: useFixedPx ? `${pxW}px` : 0,
            maxWidth: useFixedPx ? `${pxW}px` : { md: faces.length > 1 ? `calc(50% - 16px)` : '100%' },
          }}
        >
          {!useFit && faceLabel(face)}
          {!useFit && (
            <Box sx={{ position: 'relative', mt: offsetTop ? `${offsetTop}px` : 0 }}>
              <NfcPrintFace
                face={face}
                tokens={tokens}
                data={cardData}
                qrDataUrl={qrDataUrl}
                qrValue={qrValue}
                pixelWidth={pxW}
                pixelHeight={pxH}
                aspectRatio={aspectRatio}
              />
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );

  return (
    <Box sx={{ width: '100%', maxWidth: useFixedPx && !useFit ? 'none' : maxWidth, mx: 'auto', minWidth: 0 }}>
      {useFit ? (
        <Box
          sx={{
            width: contentWidth * fitScale,
            maxWidth: '100%',
            minWidth: 0,
            mx: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            alignItems: 'flex-start',
          }}
        >
          {faces.map((face) => (
            <Box key={`fit-${face.id}`} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              {faceLabel(face)}
              {scaledFaceBlock(face)}
            </Box>
          ))}
        </Box>
      ) : (
        facesRow
      )}
    </Box>
  );
}
